/**
 * Page collection Shape.
 * - Ajout rapide au panier sans rechargement (cartes shape-product-card), avec mise à jour
 *   du panier Horizon (tiroir, compteur) via l'événement CartAddEvent
 * - Apparition des cartes au défilement (y compris celles chargées par le scroll infini ou les filtres)
 * - « Lire la suite » sur la description de collection
 */
import { CartAddEvent } from '@theme/events';

/* ---------- Ajout rapide au panier ---------- */

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-shape-quick-add')) return;
  event.preventDefault();

  const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
  const label = button?.querySelector('.shape-card__button-label');
  if (!button || !label || button.classList.contains('is-loading')) return;

  const initialLabel = label.textContent;
  button.classList.add('is-loading');
  label.textContent = button.dataset.labelAdding ?? initialLabel;

  const formData = new FormData(form);
  // Sections du panier à re-rendre (tiroir panier d'Horizon)
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message);

    button.classList.remove('is-loading');
    button.classList.add('is-added');
    label.textContent = button.dataset.labelAdded ?? initialLabel;

    form.dispatchEvent(
      new CartAddEvent({}, String(formData.get('id')), {
        source: 'product-form-component',
        itemCount: Number(formData.get('quantity')) || 1,
        productId: form.closest('[data-product-id]')?.getAttribute('data-product-id') ?? undefined,
        sections: data.sections,
      })
    );
  } catch (error) {
    // En cas d'erreur (stock insuffisant…), on revient à l'envoi classique du formulaire
    console.error(error);
    button.classList.remove('is-loading');
    form.removeAttribute('data-shape-quick-add');
    form.submit();
    return;
  }

  window.setTimeout(() => {
    button.classList.remove('is-added');
    label.textContent = initialLabel;
  }, 2200);
});

/* ---------- Apparition au défilement ---------- */

// Les cartes ne sont masquées (en attente d'apparition) que si ce script tourne
document.documentElement.classList.add('shape-reveal-ready');

const revealObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      revealObserver.unobserve(entry.target);
    });
  },
  { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
);

function observeCards(root = document) {
  root.querySelectorAll('.shape-card:not(.is-visible)').forEach((card) => revealObserver.observe(card));
}

// Les cartes arrivent aussi après coup (scroll infini, filtres, tri) : on surveille la grille
new MutationObserver((mutations) => {
  if (mutations.some((mutation) => mutation.addedNodes.length)) observeCards();
}).observe(document.body, { childList: true, subtree: true });

observeCards();

/* ---------- « Lire la suite » ---------- */

document.querySelectorAll('[data-shape-expand]').forEach((wrapper) => {
  const text = wrapper.querySelector('.shape-collection-header__description-text');
  const button = /** @type {HTMLButtonElement | null} */ (wrapper.querySelector('button'));
  if (!text || !button) return;

  // Le bouton n'apparaît que si le texte dépasse les lignes affichées
  if (text.scrollHeight > text.clientHeight + 4) button.hidden = false;

  button.addEventListener('click', () => {
    const expanded = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(expanded));
    wrapper.classList.toggle('is-expanded', expanded);
    button.textContent = (expanded ? button.dataset.labelLess : button.dataset.labelMore) ?? '';
  });
});
